/**
 * Axis tick formatting for the report charts.
 *
 * The report charts draw ticks at 20px, and recharts gives a YAxis 60px of width by
 * default — enough for maybe five characters. A full "$3,538" overflows that and gets
 * clipped at the left edge of the page, so ticks are abbreviated instead.
 *
 * Distinct from the shared `_formatPrice`, which rounds thousands to whole units
 * ($3,538 -> "$4K"). Rents cluster inside a single thousand, so the axis needs the
 * fraction: $3,538 -> "$3.54K", $1,500 -> "$1.5K".
 */
export const _formatAxisPrice = (value) => {
	if (value === null || value === undefined || value === '') return value;
	const num = Number(value);
	if (!isFinite(num)) return value;
	if (num === 0) return '$0';

	const sign = num < 0 ? '-' : '';
	const abs = Math.abs(num);
	const units = [
		{ size: 1e9, suffix: 'B' },
		{ size: 1e6, suffix: 'M' },
		{ size: 1e3, suffix: 'K' },
	];

	for (let i = 0; i < units.length; i++) {
		if (abs >= units[i].size) {
			// parseFloat drops trailing zeros: 1.50 -> 1.5, 2.00 -> 2.
			const scaled = parseFloat((abs / units[i].size).toFixed(2));
			return `${sign}$${scaled}${units[i].suffix}`;
		}
	}
	return `${sign}$${Math.round(abs).toLocaleString()}`;
};

/**
 * Width the value axis needs for its widest tick. Recharts clips rather than grows, and
 * the ticks are wider than default at these font sizes.
 */
export const _axisWidth = (ticks, fontSize) => {
	const labels = (ticks || []).map(tick => String(_formatAxisPrice(tick)));
	const longest = labels.reduce((max, label) => Math.max(max, label.length), 0);
	// ~0.62em per character for the semibold numerals used here, plus the tick's dx.
	return Math.max(60, Math.ceil(longest * fontSize * 0.62) + 12);
};
